import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { UNSAFE_DataRouterContext, unstable_usePrompt as useUnsavedChangesPrompt, useBeforeUnload, useNavigate, useParams } from "react-router-dom";
import { AppState } from "../../../store/root.reducer";
import { Alert, Badge, Button, Card, Col, Collapse, Form, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import {
  GetEdmArtwork,
  GetEdmDatasets,
  GetEdmPolicy,
  RecomputeEdmArtwork,
  TransitionEdmArtwork,
  UpdateEdmArtworkProfile,
  ValidateEdmArtworkMedia,
} from "./store/admin-edm.actions";
import {
  canMarkReady,
  canPublish,
  canReturnToDraft,
  canWithdraw,
  COMMON_LANGUAGE_TAG_OPTIONS,
  formatContextValidationMessage,
  formatBlockerCode,
  formatPublicAvailabilityReason,
  formatValidationIssueCode,
  INTENDED_USAGE_URI_HELP_TEXT,
  isValidLanguageTag,
  LANGUAGE_TAG_HELP_TEXT,
} from "./edm.helpers";
import { ConfirmModal } from "../../../components/modals/confirm-modal";
import {
  AdminEdmArtworkFormState,
  AdminEdmContextRepresentation,
  AdminEdmContextSuggestion,
  AdminEdmContextValidationDetail,
  AdminEdmFieldValidationDetail,
  AdminEdmArtworkSaveErrorDetails,
} from "../../../types/adminEdm";
import { getInstitutionsRequest } from "../store/admin.api";
import { Institution } from "../../../models/institution.model";
import adminTheme from "../admin-theme.module.css";
import workspaceTheme from "../../../components/artwork-workspace/workspaceTheme.module.css";
import { EdmFieldLabel } from "./EdmFieldLabel";
import { EdmLiteralListEditor } from "./EdmLiteralListEditor";
import { EdmStringListEditor } from "./EdmStringListEditor";
import { EdmContextEntitiesEditor } from "./EdmContextEntitiesEditor";
import { EdmWorkspaceShell } from "./EdmWorkspaceShell";
import {
  clearCreatorLinksWithoutExplicitCreators,
  EDM_LITERAL_PROPERTY_MAP,
  normalizeContextEntityForForm,
  normalizeEdmLiteralListForForm,
  serializeContextEntities,
  serializeEdmLiteralList,
} from "./edm-authoring.shared";
import {
  getSafeHttpUrl,
  normalizeAdminEdmArtworkDetail,
  normalizeAdminEdmArtworkMediaValidation,
  normalizeAdminEdmContextSuggestions,
  normalizeAdminEdmParadata,
} from "./admin-edm.normalizers";
import { isArtworkNotFoundMessage } from "./admin-edm.errors";
import { hasAdminCapability } from "../../../utils/userAccess";

type PendingTransition = "draft" | "ready" | "published" | "withdrawn" | null;
type ControlledUriMode = "preset" | "custom";
type ArtworkWorkspaceSegmentId = "core-metadata" | "context" | "delivery" | "process";
type ArtworkEditorCapabilities = {
  canEditMetadata: boolean;
  canEditInstitution: boolean;
  canRunOperationalActions: boolean;
};

type ParadataLineDrafts = {
  captureEquipment: string;
  captureSoftware: string;
  processingSteps: string;
};

const CUSTOM_URI_OPTION = "__custom__";
const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave this page?";
const PARADATA_FIELD_IDS: Record<string, string> = {
  paradataUrl: "paradata-url",
  paradataConformsTo: "paradata-conforms-to",
};
const DIGITAL_CAPTURE_URI = "https://cv.iptc.org/newscodes/digitalsourcetype/digitalCapture";
const DIGITAL_CAPTURE_METHODS = new Set(["photogrammetry", "structured-light-scan", "laser-scan"]);
const DIGITAL_SOURCE_TYPE_OPTIONS = [
  { value: DIGITAL_CAPTURE_URI, label: "Digital capture" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/computationalCapture", label: "Computational capture" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/negativeFilm", label: "Negative film" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/positiveFilm", label: "Positive film" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/print", label: "Print" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/screenCapture", label: "Screen capture" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/virtualRecording", label: "Virtual recording" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/compositeCapture", label: "Composite capture" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/humanEdits", label: "Human edits" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia", label: "Composite with trained algorithmic media" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/algorithmicallyEnhanced", label: "Algorithmically enhanced" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/digitalCreation", label: "Digital creation" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/dataDrivenMedia", label: "Data-driven media" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia", label: "Trained algorithmic media" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/algorithmicMedia", label: "Algorithmic media" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/composite", label: "Composite" },
  { value: "https://cv.iptc.org/newscodes/digitalsourcetype/compositeSynthetic", label: "Composite synthetic" },
];

const EdmUnsavedChangesPrompt = ({ when, message }: { when: boolean; message: string }) => {
  useUnsavedChangesPrompt({ when, message });
  return null;
};

const createEmptyParadata = (): AdminEdmArtworkFormState["paradata"] => ({
  captureMethod: "",
  captureEquipment: [],
  captureSoftware: [],
  captureDate: "",
  operator: "",
  organisation: "",
  processingSteps: [],
  sourceAssetsSummary: "",
  qualityStatement: "",
  paradataUrl: "",
  paradataConformsTo: "",
  rightsNote: "",
});

const createEmptyTechnicalMetadata = (): AdminEdmArtworkFormState["technicalMetadata"] => ({
  vertexCount: "",
  polygonCount: "",
  pointCount: "",
  digitalSourceTypeUri: "",
  digitalSourceTypeOverrideUri: "",
  effectiveDigitalSourceTypeUri: "",
});

const coerceArtworkDescription = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry || "")).filter(Boolean).join("\n\n");
  }
  return typeof value === "string" ? value : "";
};

const buildArtworkFormState = (artworkDetail: AppState["adminEdm"]["artworkDetail"]): AdminEdmArtworkFormState => ({
  institutionId: artworkDetail?.artwork.institutionId || artworkDetail?.institution?._id || "",
  datasetKey: artworkDetail?.edmProfile.datasetKey || "",
  publicationAuthority: artworkDetail?.edmProfile.publicationAuthority || "unknown",
  defaultLanguage: artworkDetail?.edmProfile.defaultLanguage || "",
  dataProvider: artworkDetail?.edmProfile.dataProvider || "",
  material: artworkDetail?.edmProfile.material || "",
  materials: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.materials,
    EDM_LITERAL_PROPERTY_MAP.materials,
    artworkDetail?.edmProfile.defaultLanguage || "en",
    [artworkDetail?.edmProfile.material || ""]
  ),
  physicalObjectDimensions: artworkDetail?.edmProfile.physicalObjectDimensions || "",
  modelDimensions: artworkDetail?.edmProfile.modelDimensions || "",
  titles: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.titles,
    EDM_LITERAL_PROPERTY_MAP.titles,
    artworkDetail?.edmProfile.defaultLanguage || "en",
    [artworkDetail?.artwork.title || ""]
  ),
  descriptions: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.descriptions,
    EDM_LITERAL_PROPERTY_MAP.descriptions,
    artworkDetail?.edmProfile.defaultLanguage || "en",
    [coerceArtworkDescription(artworkDetail?.artwork.description)]
  ),
  creators: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.creators,
    EDM_LITERAL_PROPERTY_MAP.creators,
    artworkDetail?.edmProfile.defaultLanguage || "en"
  ),
  contributors: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.contributors,
    EDM_LITERAL_PROPERTY_MAP.contributors,
    artworkDetail?.edmProfile.defaultLanguage || "en"
  ),
  languages: Array.isArray(artworkDetail?.edmProfile.languages) ? artworkDetail.edmProfile.languages : [],
  subjects: normalizeEdmLiteralListForForm(artworkDetail?.edmProfile.subjects, EDM_LITERAL_PROPERTY_MAP.subjects, artworkDetail?.edmProfile.defaultLanguage || "en"),
  exportSourceLabelsAsSubjects: artworkDetail?.edmProfile.exportSourceLabelsAsSubjects !== false,
  publicDownloadEnabled: artworkDetail?.edmProfile.publicDownloadEnabled !== false,
  types: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.types,
    EDM_LITERAL_PROPERTY_MAP.types,
    artworkDetail?.edmProfile.defaultLanguage || "en",
    [artworkDetail?.artwork.type || ""]
  ),
  isRelatedTo: artworkDetail?.edmProfile.isRelatedTo || "",
  relatedTo: normalizeEdmLiteralListForForm(
    artworkDetail?.edmProfile.relatedTo,
    EDM_LITERAL_PROPERTY_MAP.relatedTo,
    artworkDetail?.edmProfile.defaultLanguage || "en",
    [artworkDetail?.edmProfile.isRelatedTo || ""]
  ),
  previewOverrideUrl: artworkDetail?.edmProfile.previewOverrideUrl || "",
  aggregationRightsUri: artworkDetail?.edmProfile.aggregationRightsUri || "",
  viewerStrategy: artworkDetail?.edmProfile.viewerStrategy || "direct-media",
  intendedUsageUri: artworkDetail?.edmProfile.intendedUsageUri || "",
  webResourceModelTypeUri: artworkDetail?.edmProfile.webResourceModelTypeUri || "",
  technicalMetadata: {
    ...createEmptyTechnicalMetadata(),
    vertexCount: artworkDetail?.edmProfile.technicalMetadata?.vertexCount == null ? "" : String(artworkDetail.edmProfile.technicalMetadata.vertexCount),
    polygonCount: artworkDetail?.edmProfile.technicalMetadata?.polygonCount == null ? "" : String(artworkDetail.edmProfile.technicalMetadata.polygonCount),
    pointCount: artworkDetail?.edmProfile.technicalMetadata?.pointCount == null ? "" : String(artworkDetail.edmProfile.technicalMetadata.pointCount),
    digitalSourceTypeUri: artworkDetail?.edmProfile.technicalMetadata?.digitalSourceTypeUri || "",
    digitalSourceTypeOverrideUri: artworkDetail?.edmProfile.technicalMetadata?.digitalSourceTypeOverrideUri || "",
    effectiveDigitalSourceTypeUri: artworkDetail?.edmProfile.technicalMetadata?.effectiveDigitalSourceTypeUri || "",
  },
  contextEntities: Array.isArray(artworkDetail?.edmProfile.contextEntities)
    ? artworkDetail.edmProfile.contextEntities.map((context) => normalizeContextEntityForForm(context))
    : [],
  contextSuggestions: normalizeAdminEdmContextSuggestions(artworkDetail?.edmProfile.contextSuggestions),
  paradata: artworkDetail?.edmProfile.paradata
    ? normalizeAdminEdmParadata(artworkDetail.edmProfile.paradata)
    : createEmptyParadata(),
});

const parseLineList = (value: string): string[] =>
  value
    .split(/\r?\n/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const parseOptionalInteger = (value: string): number | null => {
  const normalized = value.trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const joinLineList = (values: string[]): string => values.join("\n");

const createEmptyParadataDrafts = (): ParadataLineDrafts => ({
  captureEquipment: "",
  captureSoftware: "",
  processingSteps: "",
});

const buildParadataDrafts = (paradata: AdminEdmArtworkFormState["paradata"]): ParadataLineDrafts => ({
  captureEquipment: joinLineList(paradata.captureEquipment),
  captureSoftware: joinLineList(paradata.captureSoftware),
  processingSteps: joinLineList(paradata.processingSteps),
});

const cloneArtworkFormState = (formState: AdminEdmArtworkFormState): AdminEdmArtworkFormState =>
  JSON.parse(JSON.stringify(formState)) as AdminEdmArtworkFormState;

const resolveEffectiveDigitalSourceTypeUriForForm = (formState: AdminEdmArtworkFormState): string => {
  if (formState.technicalMetadata.digitalSourceTypeOverrideUri) {
    return formState.technicalMetadata.digitalSourceTypeOverrideUri;
  }
  if (formState.technicalMetadata.digitalSourceTypeUri) {
    return formState.technicalMetadata.digitalSourceTypeUri;
  }
  if (DIGITAL_CAPTURE_METHODS.has(formState.paradata.captureMethod)) {
    return DIGITAL_CAPTURE_URI;
  }
  return "";
};

const formatParadataSummary = (
  paradata: AdminEdmArtworkFormState["paradata"],
  options?: {
    hasLinkedParadata?: boolean;
  }
): string => {
  const linkedParadataPresent = Boolean(options?.hasLinkedParadata || paradata.paradataUrl);
  const hasAnyParadata = Boolean(
    paradata.captureMethod
    || paradata.operator
    || paradata.organisation
    || paradata.captureDate
    || paradata.captureEquipment.length > 0
    || paradata.captureSoftware.length > 0
    || paradata.processingSteps.length > 0
    || paradata.sourceAssetsSummary
    || paradata.qualityStatement
    || paradata.rightsNote
    || linkedParadataPresent
  );

  if (paradata.captureMethod) {
    return linkedParadataPresent
      ? `Method: ${paradata.captureMethod} | Link available`
      : `Method: ${paradata.captureMethod}`;
  }

  if (hasAnyParadata) {
    return linkedParadataPresent
      ? "Additional paradata captured | Link available"
      : "Additional paradata captured";
  }

  return "Not captured";
};

const buildContextValidationLookup = (details?: AdminEdmArtworkSaveErrorDetails | null) => {
  const byIndex = new Map<number, AdminEdmContextValidationDetail[]>();
  const summaryMessages: string[] = [];
  const contextDetails = details?.contextEntities || [];

  contextDetails.forEach((detail) => {
    summaryMessages.push(formatContextValidationMessage(detail));
    const existing = byIndex.get(detail.contextIndex) || [];
    existing.push({
      ...detail,
      message: formatContextValidationMessage(detail),
    });
    byIndex.set(detail.contextIndex, existing);
  });

  return { byIndex, summaryMessages };
};

const buildFieldValidationLookup = (details?: AdminEdmFieldValidationDetail[] | null) => {
  const byField = new Map<string, AdminEdmFieldValidationDetail[]>();
  const summaryMessages: string[] = [];

  (details || []).forEach((detail) => {
    summaryMessages.push(detail.message);
    const existing = byField.get(detail.field) || [];
    existing.push(detail);
    byField.set(detail.field, existing);
  });

  return { byField, summaryMessages };
};

const isDeepEqual = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const buildChangedFields = <T extends Record<string, unknown>>(
  baseline: T | null,
  next: T
): Partial<T> => {
  if (!baseline) {
    return { ...next };
  }

  return (Object.keys(next) as Array<keyof T>).reduce((changed, key) => {
    if (!isDeepEqual(next[key], baseline[key])) {
      changed[key] = next[key];
    }
    return changed;
  }, {} as Partial<T>);
};

const buildArtworkProfileDraft = (formState: AdminEdmArtworkFormState) => {
  const serializedCreators = serializeEdmLiteralList(formState.creators, EDM_LITERAL_PROPERTY_MAP.creators);
  const serializedContextEntities = clearCreatorLinksWithoutExplicitCreators(
    serializeContextEntities(formState.contextEntities),
    serializedCreators
  );

  return {
    datasetKey: formState.datasetKey || null,
    publicationAuthority: formState.publicationAuthority,
    defaultLanguage: formState.defaultLanguage || null,
    dataProvider: formState.dataProvider || null,
    exportSourceLabelsAsSubjects: formState.exportSourceLabelsAsSubjects,
    publicDownloadEnabled: formState.publicDownloadEnabled,
    material: null,
    materials: serializeEdmLiteralList(formState.materials, EDM_LITERAL_PROPERTY_MAP.materials),
    physicalObjectDimensions: formState.physicalObjectDimensions || null,
    modelDimensions: formState.modelDimensions || null,
    titles: serializeEdmLiteralList(formState.titles, EDM_LITERAL_PROPERTY_MAP.titles),
    descriptions: serializeEdmLiteralList(formState.descriptions, EDM_LITERAL_PROPERTY_MAP.descriptions),
    creators: serializedCreators,
    contributors: serializeEdmLiteralList(formState.contributors, EDM_LITERAL_PROPERTY_MAP.contributors),
    languages: formState.languages,
    subjects: serializeEdmLiteralList(formState.subjects, EDM_LITERAL_PROPERTY_MAP.subjects),
    types: serializeEdmLiteralList(formState.types, EDM_LITERAL_PROPERTY_MAP.types),
    isRelatedTo: null,
    relatedTo: serializeEdmLiteralList(formState.relatedTo, EDM_LITERAL_PROPERTY_MAP.relatedTo),
    previewOverrideUrl: formState.previewOverrideUrl || null,
    aggregationRightsUri: formState.aggregationRightsUri || null,
    viewerStrategy: formState.viewerStrategy,
    intendedUsageUri: formState.intendedUsageUri || null,
    webResourceModelTypeUri: formState.webResourceModelTypeUri || null,
    technicalMetadata: {
      vertexCount: parseOptionalInteger(formState.technicalMetadata.vertexCount),
      polygonCount: parseOptionalInteger(formState.technicalMetadata.polygonCount),
      pointCount: parseOptionalInteger(formState.technicalMetadata.pointCount),
      digitalSourceTypeUri: formState.technicalMetadata.digitalSourceTypeUri || null,
      digitalSourceTypeOverrideUri: formState.technicalMetadata.digitalSourceTypeOverrideUri || null,
    },
    contextEntities: serializedContextEntities,
    contextSuggestions: formState.contextSuggestions.map((suggestion) => ({
      ...suggestion,
      prefLabel: suggestion.prefLabel || null,
      lang: suggestion.lang || null,
      referenceUri: suggestion.referenceUri || null,
      note: suggestion.note || null,
      begin: suggestion.begin || null,
      end: suggestion.end || null,
      sourceProvider: suggestion.sourceProvider || null,
      sourceKind: suggestion.sourceKind || null,
      status: suggestion.status || "pending",
    })),
    paradata: {
      captureMethod: formState.paradata.captureMethod || null,
      captureEquipment: formState.paradata.captureEquipment,
      captureSoftware: formState.paradata.captureSoftware,
      captureDate: formState.paradata.captureDate || null,
      operator: formState.paradata.operator || null,
      organisation: formState.paradata.organisation || null,
      processingSteps: formState.paradata.processingSteps,
      sourceAssetsSummary: formState.paradata.sourceAssetsSummary || null,
      qualityStatement: formState.paradata.qualityStatement || null,
      rightsNote: formState.paradata.rightsNote || null,
      paradataUrl: formState.paradata.paradataUrl || null,
      paradataConformsTo: formState.paradata.paradataConformsTo || null,
    },
  };
};

const renderSummaryValue = (values: string[]) => values.length ? values.join(", ") : "None";

const renderLink = (label: string, href: string | null, live = true) => {
  const safeHref = getSafeHttpUrl(href);
  if (!safeHref) {
    return <div className="text-white-50">{label}: unavailable</div>;
  }

  return (
    <a
      className={`${workspaceTheme.workspaceLinkAction} text-decoration-none${live ? "" : " opacity-75"}`}
      href={safeHref}
      target="_blank"
      rel="noreferrer"
    >
      {label}
    </a>
  );
};

const renderInternalLink = (label: string, href: string | null) => (
  href
    ? <a className={`${workspaceTheme.workspaceLinkAction} text-decoration-none`} href={href}>{label}</a>
    : <div className="text-white-50">{label}: unavailable</div>
);

const renderLabelWithHelp = (
  label: string,
  tooltipId: string,
  helpText: string,
  options?: {
    htmlFor?: string;
    ariaLabel?: string;
  }
) => (
  <div className="d-flex align-items-center gap-2">
    <Form.Label htmlFor={options?.htmlFor} className="mb-0">{label}</Form.Label>
    <OverlayTrigger
      placement="top"
      overlay={(
        <Tooltip id={tooltipId}>
          {helpText}
        </Tooltip>
      )}
    >
      <Button
        variant="link"
        type="button"
        className={`p-0 text-decoration-none ${workspaceTheme.workspaceLinkAction}`}
        aria-label={options?.ariaLabel || `${label} help`}
      >
        ?
      </Button>
    </OverlayTrigger>
  </div>
);

const getRightsTierLabel = (tierEligibility?: "baseline" | "tier3" | "tier4") => {
  if (tierEligibility === "tier4") return "Tier 4 eligible";
  if (tierEligibility === "tier3") return "Tier 3 eligible";
  return "Custom / outside current allowlists";
};

const isKnownUriOption = (
  value: string,
  options: Array<{ value: string }>
) => !value || options.length === 0 || options.some((option) => option.value === value);

const resolveControlledUriValue = (
  explicitValue: string,
  effectiveValue?: string | null
) => explicitValue || effectiveValue || "";

const ensureUriOptionForDisplay = (
  value: string,
  options: Array<{ value: string; label: string }>
) => {
  if (!value || options.some((option) => option.value === value)) {
    return options;
  }

  return [{ value, label: value }, ...options];
};

const resolvePresetValue = (
  value: string,
  options: Array<{ value: string }>
) => {
  if (!value) return "";
  if (options.length === 0) return value;
  return options.some((option) => option.value === value) ? value : "";
};

const isAdminArtworkUser = (user: AppState["auth"]["user"] | null | undefined) =>
  hasAdminCapability(user as any);

const toInstitutionItems = (response: any): Institution[] => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

type AdminEdmArtworkDetailPageProps = {
  embedded?: boolean;
  activeSegment?: ArtworkWorkspaceSegmentId;
  onSegmentChange?: (segment: ArtworkWorkspaceSegmentId) => void;
};

export const AdminEdmArtworkDetailPage = ({
  embedded = false,
  activeSegment: controlledActiveSegment,
  onSegmentChange,
}: AdminEdmArtworkDetailPageProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const artworkId = params.artworkId || "";
  const currentUser = useSelector((state: AppState) => state.auth.user);
  const {
    artworkDetail: rawArtworkDetail,
    datasets,
    policy,
    loadingArtworkDetail,
    savingArtworkProfile,
    runningArtworkRecompute,
    runningArtworkTransition,
    runningArtworkValidation,
    artworkValidation: rawArtworkValidation,
    artworkDetailError,
    artworkValidationError,
    artworkTransitionError,
    artworkSaveError,
    artworkSaveErrorDetails,
  } = useSelector((state: AppState) => state.adminEdm);
  const artworkDetail = React.useMemo(
    () => normalizeAdminEdmArtworkDetail(rawArtworkDetail),
    [rawArtworkDetail]
  );
  const artworkValidation = React.useMemo(
    () => normalizeAdminEdmArtworkMediaValidation(rawArtworkValidation),
    [rawArtworkValidation]
  );

  const [formState, setFormState] = React.useState<AdminEdmArtworkFormState | null>(null);
  const [paradataDrafts, setParadataDrafts] = React.useState<ParadataLineDrafts>(createEmptyParadataDrafts());
  const [institutions, setInstitutions] = React.useState<Institution[]>([]);
  const [pendingTransition, setPendingTransition] = React.useState<PendingTransition>(null);
  const [showContexts, setShowContexts] = React.useState(false);
  const [showLiterals, setShowLiterals] = React.useState(true);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [showParadata, setShowParadata] = React.useState(false);
  const [uncontrolledActiveSegment, setUncontrolledActiveSegment] = React.useState<ArtworkWorkspaceSegmentId>("core-metadata");
  const [showMediaValidationDetails, setShowMediaValidationDetails] = React.useState(false);
  const [showRecentHistory, setShowRecentHistory] = React.useState(false);
  const [showDebugLinks, setShowDebugLinks] = React.useState(false);
  const [rightsInputMode, setRightsInputMode] = React.useState<ControlledUriMode>("preset");
  const [intendedUsageInputMode, setIntendedUsageInputMode] = React.useState<ControlledUriMode>("preset");
  const hydratedFormKeyRef = React.useRef<string | null>(null);
  const activeSegment = controlledActiveSegment || uncontrolledActiveSegment;
  const setActiveSegment = React.useCallback((segment: ArtworkWorkspaceSegmentId) => {
    if (!controlledActiveSegment) {
      setUncontrolledActiveSegment(segment);
    }
    onSegmentChange?.(segment);
  }, [controlledActiveSegment, onSegmentChange]);
  const adminArtworkUser = isAdminArtworkUser(currentUser);
  const primarySupportedArtworkType = policy?.supportedArtworkTypes?.[0] || "3dmodel";
  const artworkDetailResolved = Boolean(artworkDetail && artworkDetail.artwork.artworkId === artworkId);
  const artworkDetailNotFound = Boolean(
    artworkDetailError &&
    !loadingArtworkDetail &&
    !artworkDetailResolved &&
    isArtworkNotFoundMessage(artworkDetailError)
  );
  const artworkDetailLoadFailed = Boolean(
    artworkDetailError &&
    !loadingArtworkDetail &&
    !artworkDetailResolved &&
    !artworkDetailNotFound
  );

  React.useEffect(() => {
    if (!artworkId) return;
    dispatch(GetEdmArtwork({ artworkId }));
    dispatch(GetEdmDatasets({ enabled: true }));
    dispatch(GetEdmPolicy());
  }, [artworkId, dispatch]);

  React.useEffect(() => {
    if (!adminArtworkUser || !artworkDetailResolved) {
      setInstitutions([]);
      return;
    }

    let alive = true;
    getInstitutionsRequest()
      .then((response) => {
        if (!alive) return;
        setInstitutions(toInstitutionItems(response));
      })
      .catch(() => {
        if (!alive) return;
        setInstitutions([]);
      });

    return () => {
      alive = false;
    };
  }, [adminArtworkUser, artworkDetailResolved]);

  const nextFormState = React.useMemo(
    () => (artworkDetail ? buildArtworkFormState(artworkDetail) : null),
    [artworkDetail]
  );
  const effectiveFormState = React.useMemo(
    () => (
      formState
        ? {
          ...formState,
          paradata: {
            ...formState.paradata,
            captureEquipment: parseLineList(paradataDrafts.captureEquipment),
            captureSoftware: parseLineList(paradataDrafts.captureSoftware),
            processingSteps: parseLineList(paradataDrafts.processingSteps),
          },
        }
        : null
    ),
    [formState, paradataDrafts]
  );
  const baselineProfileDraft = React.useMemo(
    () => (nextFormState ? buildArtworkProfileDraft(nextFormState) : null),
    [nextFormState]
  );
  const workingProfileDraft = React.useMemo(
    () => (effectiveFormState ? buildArtworkProfileDraft(effectiveFormState) : null),
    [effectiveFormState]
  );
  const changedProfileDraft = React.useMemo(
    () => (workingProfileDraft ? buildChangedFields(baselineProfileDraft, workingProfileDraft) : null),
    [baselineProfileDraft, workingProfileDraft]
  );
  const nextFormStateSignature = React.useMemo(
    () => (nextFormState ? JSON.stringify(nextFormState) : null),
    [nextFormState]
  );
  const effectiveFormStateSignature = React.useMemo(
    () => (effectiveFormState ? JSON.stringify(effectiveFormState) : null),
    [effectiveFormState]
  );
  const nextFormStateKey = React.useMemo(
    () => (nextFormStateSignature ? `${artworkId}:${nextFormStateSignature}` : null),
    [artworkId, nextFormStateSignature]
  );
  const contextValidationState = React.useMemo(
    () => buildContextValidationLookup(artworkSaveErrorDetails as AdminEdmArtworkSaveErrorDetails | null),
    [artworkSaveErrorDetails]
  );
  const paradataValidationState = React.useMemo(
    () => buildFieldValidationLookup((artworkSaveErrorDetails as AdminEdmArtworkSaveErrorDetails | null)?.paradata || []),
    [artworkSaveErrorDetails]
  );
  const getParadataFieldErrors = React.useCallback(
    (field: string) => paradataValidationState.byField.get(field) || [],
    [paradataValidationState]
  );
  const knownRightsOptions = policy?.aggregationRightsOptions || [];
  const knownUsageOptions = policy?.intendedUsageOptions || [];
  const knownRightsOptionsKey = React.useMemo(
    () => knownRightsOptions.map((option) => option.value).join("|"),
    [knownRightsOptions]
  );
  const knownUsageOptionsKey = React.useMemo(
    () => knownUsageOptions.map((option) => option.value).join("|"),
    [knownUsageOptions]
  );
  const hydratedAggregationRightsUri = resolveControlledUriValue(
    nextFormState?.aggregationRightsUri || "",
    artworkDetail?.derived.effectiveAggregationRightsUri
  );
  const hydratedIntendedUsageUri = resolveControlledUriValue(
    nextFormState?.intendedUsageUri || "",
    artworkDetail?.derived.effectiveIntendedUsageUri
  );

  React.useEffect(() => {
    if (!nextFormState || !nextFormStateKey) return;
    if (hydratedFormKeyRef.current === nextFormStateKey) return;
    hydratedFormKeyRef.current = nextFormStateKey;
    setFormState(cloneArtworkFormState(nextFormState));
    setParadataDrafts(buildParadataDrafts(nextFormState.paradata));
  }, [nextFormState, nextFormStateKey]);

  React.useEffect(() => {
    if (!nextFormStateKey) return;
    setRightsInputMode(isKnownUriOption(hydratedAggregationRightsUri, knownRightsOptions) ? "preset" : "custom");
    setIntendedUsageInputMode(isKnownUriOption(hydratedIntendedUsageUri, knownUsageOptions) ? "preset" : "custom");
  }, [
    hydratedAggregationRightsUri,
    hydratedIntendedUsageUri,
    knownRightsOptionsKey,
    knownUsageOptionsKey,
    nextFormStateKey,
  ]);

  React.useEffect(() => {
    const firstInvalidField = (artworkSaveErrorDetails as AdminEdmArtworkSaveErrorDetails | null)?.paradata?.[0]?.field;
    if (!firstInvalidField) {
      return;
    }

    setActiveSegment("process");
    const inputId = PARADATA_FIELD_IDS[firstInvalidField];
    if (!inputId) {
      return;
    }

    window.requestAnimationFrame(() => {
      const field = document.getElementById(inputId) as HTMLElement | null;
      field?.focus();
      field?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }, [artworkSaveErrorDetails]);

  const isDirty = Boolean(
    effectiveFormStateSignature &&
    nextFormStateSignature &&
    effectiveFormStateSignature !== nextFormStateSignature
  );
  const dataRouterContext = React.useContext(UNSAFE_DataRouterContext);

  useBeforeUnload(
    React.useCallback((event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
    }, [isDirty])
  );

  const institutionOptions = React.useMemo(() => {
    const activeInstitutions = institutions.filter((institution) => institution.active !== false);
    const currentInstitution = artworkDetail?.institution;
    if (
      currentInstitution?._id &&
      !activeInstitutions.some((institution) => institution._id === currentInstitution._id)
    ) {
      return [
        {
          _id: currentInstitution._id,
          name: currentInstitution.name || "Inactive institution",
          slug: currentInstitution.slug || "",
          edmProviderName: currentInstitution.edmProviderName || "",
          sketchfabImportLimit: currentInstitution.sketchfabImportLimit || 10,
          active: currentInstitution.active !== false,
        },
        ...activeInstitutions,
      ];
    }

    return activeInstitutions;
  }, [artworkDetail?.institution, institutions]);

  const datasetOptions = React.useMemo(() => {
    const selectedInstitutionId = formState?.institutionId || "";
    const filtered = datasets.filter((dataset) => {
      if (!selectedInstitutionId) {
        return false;
      }
      return dataset.institutionId === selectedInstitutionId;
    });

    if (
      artworkDetail?.dataset?.datasetKey &&
      selectedInstitutionId === (artworkDetail?.institution?._id || "") &&
      !filtered.some((dataset) => dataset.datasetKey === artworkDetail.dataset?.datasetKey)
    ) {
      filtered.unshift({
        datasetKey: artworkDetail.dataset.datasetKey,
        displayName: artworkDetail.dataset.displayName || "Untitled dataset",
        institutionId: artworkDetail.institution?._id || null,
        enabled: false,
        defaultLanguage: null,
        summary: null,
        primaryProfileId: null,
        minimumMetadataTier: null,
        minimumContentTier: null,
        publishableA1: 0,
        blockedCount: 0,
        updatedAt: null,
      });
    }

    return filtered;
  }, [artworkDetail?.dataset, artworkDetail?.institution?._id, datasets, formState?.institutionId]);

  if (loadingArtworkDetail && !artworkDetail) {
    return (
      <div className="container py-4 text-white">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading artwork...
      </div>
    );
  }

  if (artworkDetailNotFound) {
    return <div className="container py-4 text-white">Artwork not found.</div>;
  }

  if (artworkDetailLoadFailed) {
    return (
      <div className="container py-4 text-white">
        <Alert variant="danger" className="mb-0">{artworkDetailError}</Alert>
      </div>
    );
  }

  if (!artworkDetailResolved) {
    return (
      <div className="container py-4 text-white">
        <Spinner animation="border" size="sm" className="me-2" />
        Initializing artwork...
      </div>
    );
  }

  if (!artworkDetail || !formState) {
    return (
      <div className="container py-4 text-white">
        <Spinner animation="border" size="sm" className="me-2" />
        Hydrating artwork editor...
      </div>
    );
  }

  const readiness = artworkDetail.readiness;
  const state = artworkDetail.edmProfile.recordState;
  const artworkType = artworkDetail.artwork.type || "";
  const isSupportedArtwork = artworkType === primarySupportedArtworkType;
  const disabledForScope = !isSupportedArtwork;
  const readinessExplanation = artworkDetail.readinessExplanation || {
    baselineBlockers: artworkDetail.readiness?.blockers?.baseline || [],
    currentTargetBlockers: [
      ...(artworkDetail.readiness?.blockers?.metadataTier || []),
      ...(artworkDetail.readiness?.blockers?.contentTier || []),
    ],
    higherTierGaps: [],
    nextTierHint: null,
  };
  const publicAvailability = artworkDetail.publicAvailability || {
    exportableNow: false,
    reason: readiness.targetMet ? "not_in_publication_state" : "not_ready",
    allowedPublicationStates: [],
  };
  const livePublicLinks = artworkDetail.livePublicLinks || artworkDetail.publicLinks;
  const plannedPublicLinks = artworkDetail.plannedPublicLinks || artworkDetail.publicLinks;
  const landingValidationUrls = new Set(
    [livePublicLinks.edmLandingUrl, plannedPublicLinks.edmLandingUrl]
      .filter((value): value is string => Boolean(value))
  );
  const sourceLinks = artworkDetail.sourceLinks || {
    previewSourceUrl: artworkDetail.artwork.media.thumbnailUrl || null,
    modelSourceUrl: artworkDetail.artwork.media.dataUrl || null,
  };
  const editorLinks = artworkDetail.editorLinks || {
    legacyArtworkEditorPath: `/admin/artworks/edit/${artworkId}`,
  };
  const baselineBlockers = readinessExplanation.baselineBlockers;
  const displayedBaselineBlockers = baselineBlockers.includes("unsupported_artwork_type")
    ? baselineBlockers.filter((code) => code !== "edm_disabled")
    : baselineBlockers;
  const currentTargetBlockers = readinessExplanation.currentTargetBlockers;
  const higherTierGaps = readinessExplanation.higherTierGaps;
  const readinessWarnings = readiness.warnings || [];
  const metadataTierDimensions = readiness.metadataTierDimensions || {
    language: readiness.metadataTierCurrent,
    enablingElements: readiness.metadataTierCurrent,
    contextualClasses: readiness.metadataTierCurrent,
  };
  const blockingIssueCount = displayedBaselineBlockers.length + currentTargetBlockers.length;
  const publicationAuthorityKnown = formState.publicationAuthority && formState.publicationAuthority !== "unknown";
  const canMarkReadyNow = canMarkReady(state, readiness, formState.publicationAuthority);
  const canPublishNow = canPublish(state, readiness, formState.publicationAuthority);
  const aggregationRightsOptions = knownRightsOptions;
  const intendedUsageOptions = knownUsageOptions;
  const modelTypeOptions = policy?.modelTypeOptions || [];
  const sourceThumbnailUrl = artworkDetail.artwork.source?.thumbnailUrl || sourceLinks.previewSourceUrl || "";
  const previewOverrideUrl = formState.previewOverrideUrl || "";
  const effectiveThumbnailUrl = previewOverrideUrl || artworkDetail.artwork.media.thumbnailUrl || sourceThumbnailUrl;
  const hasMissingThumbnailBlocker =
    baselineBlockers.includes("missing_thumbnail") ||
    currentTargetBlockers.includes("thumbnail_missing_for_tier1");
  const thumbnailNeedsRemediation = !effectiveThumbnailUrl || hasMissingThumbnailBlocker;
  const displayedAggregationRightsUri = resolveControlledUriValue(
    formState.aggregationRightsUri,
    artworkDetail.derived.effectiveAggregationRightsUri
  );
  const displayedIntendedUsageUri = resolveControlledUriValue(
    formState.intendedUsageUri,
    artworkDetail.derived.effectiveIntendedUsageUri
  );
  const displayedModelTypeUri = resolveControlledUriValue(
    formState.webResourceModelTypeUri,
    artworkDetail.derived.effectiveWebResourceModelTypeUri
  );
  const selectableAggregationRightsOptions = ensureUriOptionForDisplay(displayedAggregationRightsUri, aggregationRightsOptions);
  const selectableIntendedUsageOptions = ensureUriOptionForDisplay(displayedIntendedUsageUri, intendedUsageOptions);
  const selectableModelTypeOptions = ensureUriOptionForDisplay(displayedModelTypeUri, modelTypeOptions);
  const aggregationRightsOption = aggregationRightsOptions.find((option) => option.value === displayedAggregationRightsUri) || null;
  const knownAggregationRightsValue = resolvePresetValue(displayedAggregationRightsUri, selectableAggregationRightsOptions);
  const knownIntendedUsageValue = resolvePresetValue(displayedIntendedUsageUri, selectableIntendedUsageOptions);
  const knownModelTypeValue = resolvePresetValue(displayedModelTypeUri, selectableModelTypeOptions);
  const defaultLanguageValid = isValidLanguageTag(formState.defaultLanguage);
  const effectiveParadata = effectiveFormState?.paradata || formState.paradata;
  const effectiveDigitalSourceTypeUri = resolveEffectiveDigitalSourceTypeUriForForm(
    effectiveFormState || formState
  );
  const paradataSummary = formatParadataSummary(effectiveParadata, {
    hasLinkedParadata: Boolean(
      effectiveParadata.paradataUrl ||
      artworkDetail.livePublicLinks.paradataResourceUrl ||
      artworkDetail.plannedPublicLinks.paradataResourceUrl
    ),
  });
  const legacyPhysicalMeta = artworkDetail.artwork.physicalMeta;
  const legacyMaterialFallback = formState.materials.length === 0 ? (legacyPhysicalMeta?.materials || "") : "";
  const legacyPhysicalDimensionsFallback = !formState.physicalObjectDimensions ? (legacyPhysicalMeta?.format || "") : "";
  const exportedContexts = artworkDetail.exportedContexts || {
    presentTypes: [],
    agents: [],
    places: [],
    timeSpans: [],
    concepts: [],
    representations: [] as AdminEdmContextRepresentation[],
  };
  const pendingContextSuggestions = formState.contextSuggestions.filter((suggestion) => suggestion.status !== "dismissed");
  const contextSummaryHighlights = [
    ...exportedContexts.agents,
    ...exportedContexts.concepts,
    ...exportedContexts.timeSpans,
    ...exportedContexts.places,
  ].filter(Boolean);
  const contextSummary = exportedContexts.presentTypes.length
    ? [
      exportedContexts.presentTypes.join(", "),
      contextSummaryHighlights.slice(0, 3).join(" | "),
    ].filter(Boolean).join(" | ")
    : "None";
  const artworkEditorCapabilities: ArtworkEditorCapabilities = {
    canEditMetadata: !disabledForScope,
    canEditInstitution: adminArtworkUser && !disabledForScope,
    canRunOperationalActions: !disabledForScope,
  };
  const metadataReadOnly = !artworkEditorCapabilities.canEditMetadata;
  const operationalActionsDisabled = disabledForScope || isDirty;
  const datasetSelectionDisabled = metadataReadOnly || (!formState.institutionId && !formState.datasetKey);
  const dirtyActionMessage = isDirty ? "Save changes before running QA or publication actions." : null;
  const safeSourceThumbnailUrl = getSafeHttpUrl(sourceThumbnailUrl);
  const shouldShowValidationIssueContentType = (issue: { contentType?: string | null; url?: string | null; finalUrl?: string | null }) => {
    if (!issue.contentType) return false;
    const normalizedContentType = issue.contentType.trim().toLowerCase();
    if (!normalizedContentType.startsWith("text/html")) {
      return true;
    }
    const issueUrls = [issue.url, issue.finalUrl].filter((value): value is string => Boolean(value));
    const isLandingIssue = issueUrls.some((value) => landingValidationUrls.has(value));
    return !isLandingIssue;
  };
  const defaultLanguageChanged = Boolean(
    changedProfileDraft && Object.prototype.hasOwnProperty.call(changedProfileDraft, "defaultLanguage")
  );
  const defaultLanguageSaveBlocked = defaultLanguageChanged && !defaultLanguageValid;
  const institutionChanged = Boolean(
    artworkEditorCapabilities.canEditInstitution
    && ((effectiveFormState || formState).institutionId || "") !== (nextFormState?.institutionId || "")
  );
  const hasChangedProfileFields = Boolean(changedProfileDraft && Object.keys(changedProfileDraft).length > 0);
  const persistedCreatorLinksNeedClear = Boolean(
    nextFormState
    && nextFormState.creators.length === 0
    && nextFormState.contextEntities.some((entity) => Boolean(entity.providedChoLink))
  );

  const onSave = () => {
    if (metadataReadOnly || defaultLanguageSaveBlocked) return;

    const payload: {
      institutionId?: string | null;
      edmProfile?: Partial<ReturnType<typeof buildArtworkProfileDraft>>;
    } = {};
    const workingProfilePayload: Partial<ReturnType<typeof buildArtworkProfileDraft>> | null = hasChangedProfileFields && changedProfileDraft
      ? { ...changedProfileDraft }
      : (
        workingProfileDraft && isDirty && !institutionChanged
          ? { ...workingProfileDraft }
          : null
      );

    if (institutionChanged && artworkEditorCapabilities.canEditInstitution) {
      payload.institutionId = (effectiveFormState || formState).institutionId || null;
    }

    if (workingProfilePayload && !defaultLanguageChanged) {
      delete workingProfilePayload.defaultLanguage;
    }

    if (workingProfilePayload && workingProfileDraft && persistedCreatorLinksNeedClear) {
      workingProfilePayload.creators = workingProfileDraft.creators;
      workingProfilePayload.contextEntities = workingProfileDraft.contextEntities;
    }

    if (
      workingProfilePayload
      && (hasChangedProfileFields || (isDirty && !institutionChanged))
    ) {
      payload.edmProfile = workingProfilePayload;
    }

    if (!("institutionId" in payload) && !payload.edmProfile) {
      return;
    }

    dispatch(UpdateEdmArtworkProfile({
      artworkId,
      payload,
    }));
  };

  const onRecompute = () => {
    if (operationalActionsDisabled) return;
    dispatch(RecomputeEdmArtwork({ artworkId, payload: {} }));
  };

  const onValidate = () => {
    if (operationalActionsDisabled) return;
    dispatch(ValidateEdmArtworkMedia({ artworkId, payload: {} }));
  };

  const patchFormState = (patch: Partial<AdminEdmArtworkFormState>) => {
    setFormState((current) => (current ? { ...current, ...patch } : current));
  };

  const patchParadata = (patch: Partial<AdminEdmArtworkFormState["paradata"]>) => {
    setFormState((current) => current ? {
      ...current,
      paradata: {
        ...current.paradata,
        ...patch,
      },
    } : current);
  };

  const promoteSuggestion = (suggestion: AdminEdmContextSuggestion) => {
    setFormState((current) => current ? {
      ...current,
      contextEntities: [
        ...current.contextEntities,
        normalizeContextEntityForForm({
          type: suggestion.type,
          mode: suggestion.referenceUri ? "reference" : "described",
          referenceUri: suggestion.referenceUri || "",
          prefLabel: suggestion.prefLabel ? [{ property: "skos:prefLabel", value: suggestion.prefLabel, lang: suggestion.lang || "en" }] : [],
          note: suggestion.note || "",
          begin: suggestion.begin || "",
          end: suggestion.end || "",
        }),
      ],
      contextSuggestions: current.contextSuggestions.filter((entry) => entry.id !== suggestion.id),
    } : current);
  };

  const dismissSuggestion = (suggestionId: string) => {
    setFormState((current) => current ? {
      ...current,
      contextSuggestions: current.contextSuggestions.map((suggestion) =>
        suggestion.id === suggestionId ? { ...suggestion, status: "dismissed" } : suggestion
      ),
    } : current);
  };

  const confirmTransition = () => {
    if (!pendingTransition || operationalActionsDisabled) return;
    dispatch(TransitionEdmArtwork({
      artworkId,
      payload: { targetState: pendingTransition },
      onSuccess: () => setPendingTransition(null),
    }));
  };

  const contextSummarySection = (
    <div className="d-flex flex-column gap-2">
      <div className="text-white-50 small mb-2">Current exported-context summary</div>
      {exportedContexts.presentTypes.length === 0 ? (
        <div className="text-white-50">None</div>
      ) : (
        <Row className="g-3">
          <Col md={6}>
            <div className="d-flex flex-column gap-3">
              <div>
                <div className="text-white-50 small">Agents</div>
                <div>{renderSummaryValue(exportedContexts.agents)}</div>
              </div>
              <div>
                <div className="text-white-50 small">Concepts</div>
                <div>{renderSummaryValue(exportedContexts.concepts)}</div>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="d-flex flex-column gap-3">
              <div>
                <div className="text-white-50 small">TimeSpan</div>
                <div>{renderSummaryValue(exportedContexts.timeSpans)}</div>
              </div>
              <div>
                <div className="text-white-50 small">Places</div>
                <div>{renderSummaryValue(exportedContexts.places)}</div>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );

  const contextEditorCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Contextual Entities</Card.Header>
      <Card.Body className="d-flex flex-column gap-3">
        <div className="small text-white-50">
          Only exported contextual entities count toward Metadata B/C. Literal fields like creator, tags, and year do not count unless imported as real context entities.
        </div>
        <div className="small text-white-50">
          Agent links are opt-in. Use <span className="fw-semibold">Relation to CHO</span> when an Agent should export as a linked creator or explicit <code>edm:hasMet</code> relation.
        </div>
        {policy?.supportedVocabularySourceUrl ? (
          <div className="small text-white-50">
            Use reference URIs from Europeana-supported vocabularies when you want direct authority links.
            {" "}
            <a
              className={`${workspaceTheme.workspaceLinkAction} text-decoration-none`}
              href={policy.supportedVocabularySourceUrl}
              target="_blank"
              rel="noreferrer"
            >
              Browse supported vocabularies
            </a>
            .
          </div>
        ) : null}
        {contextValidationState.summaryMessages.length > 0 ? (
          <Alert variant="warning" className="mb-0">
            <div className="fw-semibold mb-2">Context validation issues</div>
            <ul className="mb-0 ps-3">
              {contextValidationState.summaryMessages.map((message, index) => (
                <li key={`context-validation-${index}`}>{message}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div className="small text-white-50">
          Reference URI mode links the CHO directly to a supported authority. Switch to <span className="fw-semibold">Described resource</span> if Artetra should publish the contextual class itself.
        </div>
        <EdmContextEntitiesEditor
          contexts={formState.contextEntities}
          disabled={metadataReadOnly}
          representations={exportedContexts.representations}
          validationByIndex={contextValidationState.byIndex}
          supportedVocabularies={policy?.supportedVocabularies || []}
          saveDisabled={savingArtworkProfile || defaultLanguageSaveBlocked}
          onSaveRequested={onSave}
          onChange={(nextContexts) => patchFormState({ contextEntities: nextContexts })}
        />

        {pendingContextSuggestions.length > 0 ? (
          <div className="d-flex flex-column gap-2">
            <div className="text-white-50 small">Import suggestions</div>
            {pendingContextSuggestions.map((suggestion) => (
              <div key={suggestion.id} className="rounded p-2 d-flex justify-content-between align-items-center gap-3 bg-black bg-opacity-25">
                <div>
                  <div>{suggestion.type}: {suggestion.prefLabel || suggestion.referenceUri || suggestion.id}</div>
                  <div className="small text-white-50">{[suggestion.sourceProvider, suggestion.sourceKind].filter(Boolean).join(" / ") || "Imported suggestion"}</div>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="light"
                    size="sm"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                    disabled={metadataReadOnly}
                    onClick={() => promoteSuggestion(suggestion)}
                  >
                    Promote
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSmall} ${workspaceTheme.cardDangerAction}`}
                    disabled={metadataReadOnly}
                    onClick={() => dismissSuggestion(suggestion.id)}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {contextSummarySection}
      </Card.Body>
    </Card>
  );

  const pageHeader = (
    <div className="d-flex justify-content-between align-items-start mb-4">
      <div>
        <h2 className="mb-1">{artworkDetail.artwork.title || artworkDetail.artwork.artworkId}</h2>
        <div className="d-flex gap-2 flex-wrap">
          <Badge bg="secondary">{artworkType || "unknown"}</Badge>
          <Badge bg={state === "published" ? "success" : state === "ready" ? "warning" : "secondary"}>{state}</Badge>
          {artworkDetail.effectiveProfile?.profileId ? <Badge bg="dark">{artworkDetail.effectiveProfile.profileId}</Badge> : null}
          {artworkDetail.edmProfile.datasetKey ? <Badge bg="info">{artworkDetail.dataset?.displayName || "Untitled dataset"}</Badge> : null}
          {artworkDetail.institution?.name ? <Badge bg="primary">{artworkDetail.institution.name}</Badge> : null}
        </div>
        <div className="text-white-50 small mt-2">GUID: {artworkDetail.artwork.artworkId}</div>
      </div>
    </div>
  );

  const pageAlerts = (
    <>
      {artworkDetailError && !artworkDetailNotFound ? <Alert variant="danger" className="mb-0">{artworkDetailError}</Alert> : null}
      {artworkValidationError ? <Alert variant="warning" className="mb-0">{artworkValidationError}</Alert> : null}
      {artworkTransitionError ? <Alert variant="warning" className="mb-0">{artworkTransitionError}</Alert> : null}
      {!isSupportedArtwork ? (
        <Alert variant="warning" className="mb-0">
          Europeana publishing is currently limited to 3D artworks.
        </Alert>
      ) : null}
      <Alert variant={publicAvailability.exportableNow ? "success" : "secondary"} className="mb-0">
        {formatPublicAvailabilityReason(publicAvailability.reason)}
      </Alert>
    </>
  );

  const overviewCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Overview</Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <div className="text-white-50 small">Artwork type</div>
            <div>{artworkType || "N/A"}</div>
          </Col>
          <Col md={6}>
            <div className="text-white-50 small">Publication state</div>
            <div>{state}</div>
          </Col>
          <Col md={6}>
            <div className="text-white-50 small">Institution</div>
            <div>{artworkDetail.institution?.name || "N/A"}</div>
          </Col>
          <Col md={6}>
            <div className="text-white-50 small">Dataset</div>
            <div>{artworkDetail.dataset?.displayName || "Unassigned"}</div>
          </Col>
          <Col md={6}>
            <div className="text-white-50 small">Effective profile</div>
            <div>{artworkDetail.effectiveProfile?.profileId || "N/A"}</div>
          </Col>
          <Col md={6}>
            <div className="text-white-50 small">Contextual entities</div>
            <div>{contextSummary}</div>
          </Col>
          <Col md={12}>
            <div className="text-white-50 small">Paradata</div>
            <div>{paradataSummary}</div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const coreMetadataBasicsContent = (
    <>
      <Card bg="dark" text="light" className="mb-4">
        <Card.Header>EDM Assignment</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label htmlFor="artwork-dataset-select">Dataset</Form.Label>
              <Form.Select
                id="artwork-dataset-select"
                className={workspaceTheme.workspaceSelect}
                value={formState.datasetKey}
                disabled={datasetSelectionDisabled}
                onChange={(event) => patchFormState({ datasetKey: event.target.value })}
              >
                <option value="">Unassigned</option>
                {datasetOptions.map((dataset) => (
                  <option key={dataset.datasetKey} value={dataset.datasetKey}>
                    {dataset.displayName || "Untitled dataset"}
                  </option>
                ))}
              </Form.Select>
              {!formState.institutionId ? (
                <Form.Text className="text-white-50">
                  Choose an institution before assigning this artwork to a dataset.
                </Form.Text>
              ) : null}
            </Col>
            <Col md={6}>
              <Form.Label htmlFor="artwork-publication-authority">Publication authority</Form.Label>
              <Form.Select
                id="artwork-publication-authority"
                className={workspaceTheme.workspaceSelect}
                value={formState.publicationAuthority}
                disabled={metadataReadOnly}
                onChange={(event) => patchFormState({ publicationAuthority: event.target.value })}
              >
                <option value="unknown">unknown</option>
                <option value="owner-submitted">owner-submitted</option>
                <option value="institution-submitted">institution-submitted</option>
                <option value="authorized-import">authorized-import</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label htmlFor="artwork-institution-select">Institution</Form.Label>
              {artworkEditorCapabilities.canEditInstitution ? (
                <Form.Select
                  id="artwork-institution-select"
                  className={workspaceTheme.workspaceSelect}
                  value={formState.institutionId}
                  disabled={metadataReadOnly}
                  onChange={(event) => {
                    const nextInstitutionId = event.target.value;
                    const selectedDataset = datasets.find((dataset) => dataset.datasetKey === formState.datasetKey);
                    const nextDatasetKey =
                      selectedDataset && selectedDataset.institutionId === nextInstitutionId
                        ? formState.datasetKey
                        : "";

                    patchFormState({
                      institutionId: nextInstitutionId,
                      datasetKey: nextDatasetKey,
                    });
                  }}
                >
                  <option value="">Unassigned</option>
                  {institutionOptions.map((institution) => (
                    <option key={institution._id} value={institution._id}>
                      {institution.active === false ? `${institution.name} (inactive)` : institution.name}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control value={artworkDetail.institution?.name || ""} readOnly className={adminTheme.darkReadOnlyField} />
              )}
            </Col>
            <Col md={6}>
              <EdmFieldLabel
                label="Default language tag override"
                htmlFor="artwork-default-language"
                helpText={LANGUAGE_TAG_HELP_TEXT}
                tooltipId="artwork-default-language-help"
                ariaLabel="Language tag help"
              />
              <Form.Control
                id="artwork-default-language"
                list="edm-language-tag-options"
                value={formState.defaultLanguage}
                disabled={metadataReadOnly}
                isInvalid={defaultLanguageSaveBlocked}
                placeholder="en"
                onChange={(event) => patchFormState({ defaultLanguage: event.target.value })}
              />
              <datalist id="edm-language-tag-options">
                {COMMON_LANGUAGE_TAG_OPTIONS.map((languageTag) => (
                  <option key={languageTag} value={languageTag} />
                ))}
              </datalist>
              <Form.Control.Feedback type="invalid">
                Enter a valid BCP47 language tag such as en, ro, en-GB, or pt-BR.
              </Form.Control.Feedback>
            </Col>
            <Col md={6}>
              <EdmFieldLabel
                label="Data Provider"
                htmlFor="artwork-data-provider"
                helpText="Exports as ore:Aggregation / edm:dataProvider."
                tooltipId="artwork-data-provider-help"
              />
              <Form.Control
                id="artwork-data-provider"
                value={formState.dataProvider}
                disabled={metadataReadOnly}
                onChange={(event) => patchFormState({ dataProvider: event.target.value })}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card bg="dark" text="light" className="mb-4">
        <Card.Header>Object Metadata</Card.Header>
        <Card.Body>
          <div className="d-flex flex-column gap-3">
            <Row className="g-3">
              <Col md={6}>
                <EdmFieldLabel
                  label="Physical object dimensions"
                  htmlFor="artwork-edm-physical-dimensions"
                  helpText="Exports as edm:ProvidedCHO / dcterms:extent."
                  tooltipId="artwork-edm-physical-dimensions-help"
                />
                <Form.Control
                  id="artwork-edm-physical-dimensions"
                  value={formState.physicalObjectDimensions}
                  placeholder={legacyPhysicalDimensionsFallback || undefined}
                  disabled={metadataReadOnly}
                  onChange={(event) => patchFormState({ physicalObjectDimensions: event.target.value })}
                />
                {legacyPhysicalDimensionsFallback ? (
                  <Form.Text className="text-white-50 d-block">
                    Legacy physical metadata fallback: {legacyPhysicalDimensionsFallback}
                  </Form.Text>
                ) : null}
              </Col>
              <Col md={6}>
                <EdmFieldLabel
                  label="3D object dimensions"
                  htmlFor="artwork-edm-model-dimensions"
                  helpText="Exports as edm:WebResource / dcterms:extent."
                  tooltipId="artwork-edm-model-dimensions-help"
                />
                <Form.Control
                  id="artwork-edm-model-dimensions"
                  value={formState.modelDimensions}
                  disabled={metadataReadOnly}
                  onChange={(event) => patchFormState({ modelDimensions: event.target.value })}
                />
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

    </>
  );

  const edmLiteralsCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>EDM Literals</Card.Header>
      <Card.Body className="d-flex flex-column gap-3">
        <div className="small text-white-50">
          Literal creator names are authored here. Linked creator or related-agent resources are configured in <span className="fw-semibold">Context</span>.
        </div>
        <Row className="g-3">
          <Col xl={6}>
            <EdmLiteralListEditor
              label="Titles"
              labelHelpText="Exports as edm:ProvidedCHO / dc:title."
              property={EDM_LITERAL_PROPERTY_MAP.titles}
              values={formState.titles}
              disabled={metadataReadOnly}
              defaultLanguage={formState.defaultLanguage || "en"}
              onChange={(titles) => patchFormState({ titles })}
            />
          </Col>
          <Col xl={6}>
            <EdmLiteralListEditor
              label="Creators"
              labelHelpText="Exports as edm:ProvidedCHO / dc:creator."
              property={EDM_LITERAL_PROPERTY_MAP.creators}
              values={formState.creators}
              disabled={metadataReadOnly}
              defaultLanguage={formState.defaultLanguage || "en"}
              onChange={(creators) => patchFormState({ creators })}
            />
          </Col>
          <Col xl={6}>
            <EdmLiteralListEditor
              label="Contributors"
              labelHelpText="Exports as edm:ProvidedCHO / dc:contributor."
              property={EDM_LITERAL_PROPERTY_MAP.contributors}
              values={formState.contributors}
              disabled={metadataReadOnly}
              defaultLanguage={formState.defaultLanguage || "en"}
              onChange={(contributors) => patchFormState({ contributors })}
            />
          </Col>
          <Col xl={6}>
            <EdmStringListEditor
              label="Languages"
              helpText="Exports as edm:ProvidedCHO / dc:language. Use ISO 639 codes such as en, ro, fra, or zxx."
              values={formState.languages}
              placeholder="en"
              disabled={metadataReadOnly}
              onChange={(languages) => patchFormState({ languages })}
            />
          </Col>
          <Col xl={6}>
            <div className="d-flex flex-column gap-2 h-100">
              <EdmLiteralListEditor
                label="Materials"
                labelHelpText="Exports as edm:ProvidedCHO / dcterms:medium."
                property={EDM_LITERAL_PROPERTY_MAP.materials}
                values={formState.materials}
                disabled={metadataReadOnly}
                defaultLanguage={formState.defaultLanguage || "en"}
                onChange={(materials) => patchFormState({ materials })}
              />
              {legacyMaterialFallback ? (
                <Form.Text className="text-white-50 d-block mb-0">
                  Legacy physical metadata fallback: {legacyMaterialFallback}
                </Form.Text>
              ) : null}
            </div>
          </Col>
          <Col xl={12}>
            <EdmLiteralListEditor
              label="Descriptions"
              labelHelpText="Exports as edm:ProvidedCHO / dc:description."
              property={EDM_LITERAL_PROPERTY_MAP.descriptions}
              values={formState.descriptions}
              disabled={metadataReadOnly}
              defaultLanguage={formState.defaultLanguage || "en"}
              onChange={(descriptions) => patchFormState({ descriptions })}
            />
          </Col>
          <Col xl={6}>
            <EdmLiteralListEditor
              label="Types"
              labelHelpText="Exports as edm:ProvidedCHO / dc:type."
              property={EDM_LITERAL_PROPERTY_MAP.types}
              values={formState.types}
              disabled={metadataReadOnly}
              defaultLanguage={formState.defaultLanguage || "en"}
              onChange={(types) => patchFormState({ types })}
            />
          </Col>
          <Col xl={6}>
            <div className="d-flex flex-column gap-3">
              <EdmLiteralListEditor
                label="Subjects"
                labelHelpText="Exports as edm:ProvidedCHO / dc:subject."
                property={EDM_LITERAL_PROPERTY_MAP.subjects}
                values={formState.subjects}
                disabled={metadataReadOnly}
                defaultLanguage={formState.defaultLanguage || "en"}
                footer={(
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-start gap-2">
                      <Form.Check
                        id="export-source-labels-as-subjects"
                        className="mb-0 flex-shrink-0"
                        type="checkbox"
                        checked={formState.exportSourceLabelsAsSubjects}
                        disabled={metadataReadOnly}
                        onChange={(event) => patchFormState({ exportSourceLabelsAsSubjects: event.target.checked })}
                        aria-label="Export imported source labels as fallback subjects"
                      />
                      <div className="mb-0">
                        <Form.Label htmlFor="export-source-labels-as-subjects" className="mb-0 d-inline">
                          Export imported source labels as fallback subjects
                        </Form.Label>
                        {" "}
                        <OverlayTrigger
                          placement="top"
                          trigger={["hover", "focus", "click"]}
                          overlay={(
                            <Tooltip id="export-source-labels-as-subjects-help">
                              When explicit Subjects are empty, imported provider labels can still export as literal dc:subject values. Disable this to suppress source-label fallback while keeping manual Subjects and artwork tags unchanged.
                            </Tooltip>
                          )}
                        >
                          <Button
                            variant="link"
                            type="button"
                            className={`p-0 text-decoration-none d-inline-flex align-middle ${workspaceTheme.workspaceLinkAction}`}
                            aria-label="Fallback subject export help"
                          >
                            <InfoCircle size={14} aria-hidden="true" />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>
                )}
                onChange={(subjects) => patchFormState({ subjects })}
              />
            </div>
          </Col>
          <Col xl={12}>
            <EdmLiteralListEditor
              label="Related To"
              labelHelpText="Exports as literal edm:ProvidedCHO / edm:isRelatedTo."
              property={EDM_LITERAL_PROPERTY_MAP.relatedTo}
              values={formState.relatedTo}
              disabled={metadataReadOnly}
              defaultLanguage={formState.defaultLanguage || "en"}
              onChange={(relatedTo) => patchFormState({ relatedTo })}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const coreMetadataContent = (
    <>
      {coreMetadataBasicsContent}
      {edmLiteralsCard}
    </>
  );

  const thumbnailCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Thumbnail</Card.Header>
      <Card.Body>
        <div className="text-white-50 small">Current thumbnail</div>
        <div className="mb-3 fw-semibold">
          {effectiveThumbnailUrl ? (previewOverrideUrl ? "From EDM override" : "From source") : "Missing"}
        </div>
        {thumbnailNeedsRemediation ? (
          <Alert variant="warning" className="mb-3">
            A thumbnail is required for Content Tier 1.
          </Alert>
        ) : null}
        {effectiveThumbnailUrl ? (
          <div className="mb-3">
            <img
              src={effectiveThumbnailUrl}
              alt="Thumbnail preview"
              style={{
                display: "block",
                width: "100%",
                maxWidth: "240px",
                maxHeight: "180px",
                objectFit: "contain",
                borderRadius: "0.5rem",
              }}
            />
          </div>
        ) : null}
        <div className="text-white-50 small">Effective preview URL</div>
        <div className="text-break small mb-2">{effectiveThumbnailUrl || "N/A"}</div>
        {sourceThumbnailUrl ? (
          <>
            <div className="text-white-50 small">Source thumbnail URL</div>
            <div className="text-break small mb-3">{sourceThumbnailUrl}</div>
          </>
        ) : null}
        <EdmFieldLabel
          label="Preview override URL"
          htmlFor="preview-override-url"
          helpText="Optional EDM-only preview URL when the source thumbnail is missing or should not be used."
          tooltipId="preview-override-url-help"
        />
        <Form.Control
          id="preview-override-url"
          placeholder="https://example.com/thumbnail.png"
          value={formState.previewOverrideUrl}
          disabled={metadataReadOnly}
          onChange={(event) => patchFormState({ previewOverrideUrl: event.target.value })}
        />
        <div className="d-flex gap-2 flex-wrap mt-3">
          {previewOverrideUrl ? (
            <Button
              variant="light"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
              onClick={() => patchFormState({ previewOverrideUrl: "" })}
              disabled={metadataReadOnly}
            >
              Clear override
            </Button>
          ) : null}
          {safeSourceThumbnailUrl ? (
            <Button
              as="a"
              variant="light"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
              href={safeSourceThumbnailUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open source thumbnail
            </Button>
          ) : null}
        </div>
        {effectiveThumbnailUrl && !livePublicLinks.previewWebResourceUrl ? (
          <Alert variant="secondary" className="mt-3 mb-0">
            A thumbnail exists, but the public preview is not live yet.
          </Alert>
        ) : null}
      </Card.Body>
    </Card>
  );

  const rightsAndDeliveryCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Rights and Delivery</Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={12}>
            <div className="text-white-50 small">Exported object identifier</div>
            <div className="text-break">{`artetra:${artworkDetail.artwork.artworkId}`}</div>
          </Col>
          <Col md={6}>
            <EdmFieldLabel
              label="Aggregation rights URI"
              helpText="Choose the public aggregation-rights value exported for this EDM record."
              tooltipId="aggregation-rights-uri-help"
            />
            <Form.Select
              className={workspaceTheme.workspaceSelect}
              value={rightsInputMode === "custom" ? CUSTOM_URI_OPTION : knownAggregationRightsValue}
              disabled={metadataReadOnly}
              onChange={(event) => {
                const nextValue = event.target.value;
                if (nextValue === CUSTOM_URI_OPTION) {
                  setRightsInputMode("custom");
                  if (knownAggregationRightsValue) {
                    patchFormState({ aggregationRightsUri: "" });
                  }
                  return;
                }
                setRightsInputMode("preset");
                patchFormState({ aggregationRightsUri: nextValue });
              }}
            >
              <option value="">Unspecified</option>
              {selectableAggregationRightsOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <option value={CUSTOM_URI_OPTION}>Custom rights URI</option>
            </Form.Select>
            {rightsInputMode === "custom" ? (
              <Form.Control
                className="mt-2"
                placeholder="https://example.com/rights"
                value={formState.aggregationRightsUri || (knownAggregationRightsValue ? "" : displayedAggregationRightsUri)}
                disabled={metadataReadOnly}
                onChange={(event) => patchFormState({ aggregationRightsUri: event.target.value })}
              />
            ) : null}
            <Form.Text className="text-white-50">
              {displayedAggregationRightsUri
                ? getRightsTierLabel(aggregationRightsOption?.tierEligibility)
                : "Unspecified"}
            </Form.Text>
          </Col>
          <Col md={6}>
            <div className="text-white-50 small">Viewer strategy</div>
            <div className="fw-semibold">{formState.viewerStrategy}</div>
          </Col>
          <Col md={12}>
            <div className="d-flex align-items-start gap-2 p-3 rounded border border-secondary border-opacity-25 bg-black bg-opacity-25">
              <Form.Check
                id="public-download-enabled"
                className="mb-0 flex-shrink-0"
                type="checkbox"
                checked={formState.publicDownloadEnabled}
                disabled={metadataReadOnly}
                onChange={(event) => patchFormState({ publicDownloadEnabled: event.target.checked })}
                aria-label="Enable public download"
              />
              <div className="d-flex flex-column gap-1">
                <Form.Label htmlFor="public-download-enabled" className="mb-0 fw-semibold">
                  Enable public download
                </Form.Label>
                <Form.Text className="text-white-50 m-0">
                  Controls direct public download actions in the public artwork view and the EDM landing page. The embedded viewer remains available.
                </Form.Text>
              </div>
            </div>
          </Col>
          <Col md={12}>
            <div className="text-white-50 small">Current public rights</div>
            <div className="text-break">{artworkDetail.derived.currentPublicRights || "N/A"}</div>
          </Col>
          <Col md={12}>
            <div className="text-white-50 small">Effective landing page</div>
            <div className="text-break">{artworkDetail.derived.landingPageUrl}</div>
          </Col>
          <Col md={12}>
            <div className="text-white-50 small">Public embed URL</div>
            <div className="text-break">{plannedPublicLinks.publicEmbedUrl || "N/A"}</div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const advancedTierMetadataCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Advanced Tier Metadata</Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <EdmFieldLabel
              label="Intended usage URI"
              helpText={INTENDED_USAGE_URI_HELP_TEXT}
              tooltipId="intended-usage-uri-help"
              ariaLabel="Intended usage help"
            />
            <Form.Select
              className={workspaceTheme.workspaceSelect}
              value={intendedUsageInputMode === "custom" ? CUSTOM_URI_OPTION : knownIntendedUsageValue}
              disabled={metadataReadOnly}
              onChange={(event) => {
                const nextValue = event.target.value;
                if (nextValue === CUSTOM_URI_OPTION) {
                  setIntendedUsageInputMode("custom");
                  if (knownIntendedUsageValue) {
                    patchFormState({ intendedUsageUri: "" });
                  }
                  return;
                }
                setIntendedUsageInputMode("preset");
                patchFormState({ intendedUsageUri: nextValue });
              }}
            >
              <option value="">Unspecified</option>
              {selectableIntendedUsageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <option value={CUSTOM_URI_OPTION}>Custom intended usage URI</option>
            </Form.Select>
            {intendedUsageInputMode === "custom" ? (
              <Form.Control
                className="mt-2"
                placeholder="http://data.europeana.eu/vocabulary/usageArea/..."
                value={formState.intendedUsageUri || (knownIntendedUsageValue ? "" : displayedIntendedUsageUri)}
                disabled={metadataReadOnly}
                onChange={(event) => patchFormState({ intendedUsageUri: event.target.value })}
              />
            ) : null}
          </Col>
          <Col md={6}>
            <EdmFieldLabel
              label="WebResource model type URI"
              helpText="Use the model-type URI that matches the technical nature of the delivered 3D asset."
              tooltipId="webresource-model-type-help"
            />
            <Form.Select
              className={workspaceTheme.workspaceSelect}
              value={knownModelTypeValue}
              disabled={metadataReadOnly}
              onChange={(event) => patchFormState({ webResourceModelTypeUri: event.target.value })}
            >
              <option value="">Unspecified</option>
              {selectableModelTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {displayedModelTypeUri && !knownModelTypeValue ? (
              <Alert variant="secondary" className="mt-2 mb-0">
                The stored model type URI is not in the current supported option list. Saving without changing this field will preserve the existing value.
              </Alert>
            ) : null}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const technicalMetadataCard = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Technical Metadata</Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={4}>
            <EdmFieldLabel
              label="Polygon count"
              htmlFor="technical-metadata-polygon-count"
              helpText="Optional delivered-asset polygon count exported on the model WebResource."
              tooltipId="technical-metadata-polygon-count-help"
            />
            <Form.Control
              id="technical-metadata-polygon-count"
              inputMode="numeric"
              value={formState.technicalMetadata.polygonCount}
              placeholder="Optional"
              disabled={metadataReadOnly}
              onChange={(event) => patchFormState({
                technicalMetadata: {
                  ...formState.technicalMetadata,
                  polygonCount: event.target.value,
                },
              })}
            />
          </Col>
          <Col md={4}>
            <EdmFieldLabel
              label="Vertex count"
              htmlFor="technical-metadata-vertex-count"
              helpText="Optional delivered-asset vertex count exported on the model WebResource."
              tooltipId="technical-metadata-vertex-count-help"
            />
            <Form.Control
              id="technical-metadata-vertex-count"
              inputMode="numeric"
              value={formState.technicalMetadata.vertexCount}
              placeholder="Optional"
              disabled={metadataReadOnly}
              onChange={(event) => patchFormState({
                technicalMetadata: {
                  ...formState.technicalMetadata,
                  vertexCount: event.target.value,
                },
              })}
            />
          </Col>
          <Col md={4}>
            <EdmFieldLabel
              label="Point count"
              htmlFor="technical-metadata-point-count"
              helpText="Optional delivered-asset point count exported on the model WebResource."
              tooltipId="technical-metadata-point-count-help"
            />
            <Form.Control
              id="technical-metadata-point-count"
              inputMode="numeric"
              value={formState.technicalMetadata.pointCount}
              placeholder="Optional"
              disabled={metadataReadOnly}
              onChange={(event) => patchFormState({
                technicalMetadata: {
                  ...formState.technicalMetadata,
                  pointCount: event.target.value,
                },
              })}
            />
          </Col>
          <Col md={12}>
            <EdmFieldLabel
              label="Digital source type override"
              htmlFor="technical-metadata-digital-source-type"
              helpText="Choose an explicit IPTC digital source type only when the imported or inferred value should be overridden."
              tooltipId="technical-metadata-digital-source-type-help"
            />
            <Form.Select
              id="technical-metadata-digital-source-type"
              className={workspaceTheme.workspaceSelect}
              value={formState.technicalMetadata.digitalSourceTypeOverrideUri}
              disabled={metadataReadOnly}
              onChange={(event) => patchFormState({
                technicalMetadata: {
                  ...formState.technicalMetadata,
                  digitalSourceTypeOverrideUri: event.target.value,
                },
              })}
            >
              <option value="">Auto</option>
              {DIGITAL_SOURCE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-white-50 d-block">
              Auto keeps the imported or inferred value. Choose an IPTC value here only when you need an explicit override.
            </Form.Text>
          </Col>
          <Col md={12}>
            <Form.Text className="text-white-50 d-block mb-3">
              These values describe the delivered WebResource, not the source capture process. Leave fields blank when the counts are unknown.
            </Form.Text>
            <Row className="g-3">
              <Col md={6}>
                <div className="text-white-50 small">Effective digital source type URI</div>
                <div className="text-break small">{effectiveDigitalSourceTypeUri || "N/A"}</div>
              </Col>
              <Col md={6}>
                <div className="text-white-50 small">Stored / imported digital source type URI</div>
                <div className="text-break small">{formState.technicalMetadata.digitalSourceTypeUri || "N/A"}</div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const deliveryContent = (
    <>
      {thumbnailCard}
      {rightsAndDeliveryCard}
      {advancedTierMetadataCard}
      {technicalMetadataCard}
    </>
  );

  const processContent = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header>Paradata / Creation Process</Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label htmlFor="paradata-capture-method">Capture method</Form.Label>
            <Form.Select id="paradata-capture-method" className={workspaceTheme.workspaceSelect} value={formState.paradata.captureMethod} disabled={metadataReadOnly} onChange={(event) => patchParadata({ captureMethod: event.target.value as AdminEdmArtworkFormState["paradata"]["captureMethod"] })}>
              <option value="">Unspecified</option>
              <option value="photogrammetry">photogrammetry</option>
              <option value="structured-light-scan">structured-light-scan</option>
              <option value="laser-scan">laser-scan</option>
              <option value="manual-3d-modelling">manual-3d-modelling</option>
              <option value="bim">bim</option>
              <option value="other">other</option>
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-capture-date">Capture date</Form.Label>
            <Form.Control id="paradata-capture-date" value={formState.paradata.captureDate} disabled={metadataReadOnly} onChange={(event) => patchParadata({ captureDate: event.target.value })} />
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-operator">Operator</Form.Label>
            <Form.Control id="paradata-operator" value={formState.paradata.operator} disabled={metadataReadOnly} onChange={(event) => patchParadata({ operator: event.target.value })} />
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-organisation">Organisation</Form.Label>
            <Form.Control id="paradata-organisation" value={formState.paradata.organisation} disabled={metadataReadOnly} onChange={(event) => patchParadata({ organisation: event.target.value })} />
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-equipment">Equipment (one per line)</Form.Label>
            <Form.Control
              id="paradata-equipment"
              as="textarea"
              rows={3}
              value={paradataDrafts.captureEquipment}
              disabled={metadataReadOnly}
              onChange={(event) => setParadataDrafts((current) => ({ ...current, captureEquipment: event.target.value }))}
            />
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-software">Software (one per line)</Form.Label>
            <Form.Control
              id="paradata-software"
              as="textarea"
              rows={3}
              value={paradataDrafts.captureSoftware}
              disabled={metadataReadOnly}
              onChange={(event) => setParadataDrafts((current) => ({ ...current, captureSoftware: event.target.value }))}
            />
          </Col>
          <Col md={12}>
            <Form.Label htmlFor="paradata-processing-steps">Processing steps (one per line)</Form.Label>
            <Form.Control
              id="paradata-processing-steps"
              as="textarea"
              rows={3}
              value={paradataDrafts.processingSteps}
              disabled={metadataReadOnly}
              onChange={(event) => setParadataDrafts((current) => ({ ...current, processingSteps: event.target.value }))}
            />
          </Col>
          <Col md={12}>
            <Form.Label htmlFor="paradata-source-assets-summary">Source assets summary</Form.Label>
            <Form.Control id="paradata-source-assets-summary" as="textarea" rows={2} value={formState.paradata.sourceAssetsSummary} disabled={metadataReadOnly} onChange={(event) => patchParadata({ sourceAssetsSummary: event.target.value })} />
          </Col>
          <Col md={12}>
            <Form.Label htmlFor="paradata-quality-statement">Quality statement</Form.Label>
            <Form.Control id="paradata-quality-statement" as="textarea" rows={2} value={formState.paradata.qualityStatement} disabled={metadataReadOnly} onChange={(event) => patchParadata({ qualityStatement: event.target.value })} />
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-url">Paradata URL</Form.Label>
            <Form.Control
              id="paradata-url"
              value={formState.paradata.paradataUrl}
              disabled={metadataReadOnly}
              isInvalid={getParadataFieldErrors("paradataUrl").length > 0}
              onChange={(event) => patchParadata({ paradataUrl: event.target.value })}
            />
            {getParadataFieldErrors("paradataUrl").map((detail, index) => (
              <Form.Control.Feedback key={`paradata-url-${index}`} type="invalid">
                {detail.message}
              </Form.Control.Feedback>
            ))}
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="paradata-conforms-to">Paradata format / conformsTo</Form.Label>
            <Form.Control
              id="paradata-conforms-to"
              value={formState.paradata.paradataConformsTo}
              disabled={metadataReadOnly}
              isInvalid={getParadataFieldErrors("paradataConformsTo").length > 0}
              onChange={(event) => patchParadata({ paradataConformsTo: event.target.value })}
            />
            {getParadataFieldErrors("paradataConformsTo").map((detail, index) => (
              <Form.Control.Feedback key={`paradata-conforms-to-${index}`} type="invalid">
                {detail.message}
              </Form.Control.Feedback>
            ))}
          </Col>
          <Col md={12}>
            <Form.Label htmlFor="paradata-rights-note">Rights note</Form.Label>
            <Form.Control id="paradata-rights-note" value={formState.paradata.rightsNote} disabled={metadataReadOnly} onChange={(event) => patchParadata({ rightsNote: event.target.value })} />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const debugSection = (
    <Card bg="dark" text="light" className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Debug / Public Links</span>
        <Button
          variant="light"
          size="sm"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
          onClick={() => setShowDebugLinks((current) => !current)}
        >
          {showDebugLinks ? "Hide" : "Show"}
        </Button>
      </Card.Header>
      <Collapse in={showDebugLinks} mountOnEnter unmountOnExit>
        <div>
          <Card.Body className="d-flex flex-column gap-4">
            <Row className="g-4">
              <Col lg={6}>
                <div className="d-flex flex-column gap-2 h-100">
                  <div className="text-white-50 small text-uppercase">Live Public Surface</div>
                  <div className="d-flex flex-column gap-2">
                    {renderLink("Open public embed page", livePublicLinks.publicEmbedUrl)}
                    {renderLink("Open EDM landing page", livePublicLinks.edmLandingUrl)}
                    {renderLink("Open RDF/XML record", livePublicLinks.rdfRecordUrl)}
                    {renderLink("Open OAI GetRecord", livePublicLinks.oaiGetRecordUrl)}
                    {renderLink("Open oEmbed JSON", livePublicLinks.oembedJsonUrl)}
                    {renderLink("Open oEmbed XML", livePublicLinks.oembedXmlUrl)}
                    {renderLink("Open preview WebResource", livePublicLinks.previewWebResourceUrl)}
                    {renderLink("Open model WebResource", livePublicLinks.modelWebResourceUrl)}
                    {renderLink("Open paradata resource", livePublicLinks.paradataResourceUrl)}
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <div className="d-flex flex-column gap-2 h-100">
                  <div className="text-white-50 small text-uppercase">Planned Public Surface</div>
                  <div className="d-flex flex-column gap-2">
                    {renderLink("Planned public embed page", plannedPublicLinks.publicEmbedUrl, false)}
                    {renderLink("Planned EDM landing page", plannedPublicLinks.edmLandingUrl, false)}
                    {renderLink("Planned RDF/XML record", plannedPublicLinks.rdfRecordUrl, false)}
                    {renderLink("Planned OAI GetRecord", plannedPublicLinks.oaiGetRecordUrl, false)}
                    {renderLink("Planned oEmbed JSON", plannedPublicLinks.oembedJsonUrl, false)}
                    {renderLink("Planned oEmbed XML", plannedPublicLinks.oembedXmlUrl, false)}
                    {renderLink("Planned preview WebResource", plannedPublicLinks.previewWebResourceUrl, false)}
                    {renderLink("Planned model WebResource", plannedPublicLinks.modelWebResourceUrl, false)}
                    {renderLink("Planned paradata resource", plannedPublicLinks.paradataResourceUrl, false)}
                  </div>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex flex-column gap-2">
                  <div className="text-white-50 small text-uppercase">Source / Debug Links</div>
                  <Row className="g-2">
                    <Col md={6}>{renderLink("Open preview source", sourceLinks.previewSourceUrl)}</Col>
                    <Col md={6}>{renderLink("Open model source", sourceLinks.modelSourceUrl)}</Col>
                    <Col md={6}>{renderInternalLink("Open raw artwork JSON editor", editorLinks.legacyArtworkEditorPath)}</Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );

  const readinessCard = (
    <Card bg="dark" text="light">
      <Card.Header>Readiness Summary</Card.Header>
      <Card.Body>
        <div className="d-flex gap-2 flex-wrap mb-3">
          <Badge bg={readiness.edmCompatibilityStatus === "pass" ? "success" : "danger"}>
            {readiness.edmCompatibilityStatus === "pass" ? "Baseline Pass" : "Baseline Fail"}
          </Badge>
          <Badge bg="secondary">Metadata {readiness.metadataTierCurrent}</Badge>
          <Badge bg="secondary">Content {readiness.contentTierCurrent}</Badge>
          <Badge bg={readiness.targetMet ? "success" : "warning"}>{readiness.targetMet ? "Target Met" : "Target Not Met"}</Badge>
        </div>
        <div className="small text-white-50">Metadata dimensions</div>
        <div className="d-flex gap-2 flex-wrap mb-3">
          <Badge bg="secondary">Language {metadataTierDimensions.language}</Badge>
          <Badge bg="secondary">Enabling {metadataTierDimensions.enablingElements}</Badge>
          <Badge bg="secondary">Contextual {metadataTierDimensions.contextualClasses}</Badge>
        </div>
        {readinessWarnings.length > 0 ? (
          <div className="d-flex flex-column gap-2 mb-3">
            <div className="small text-white-50">Warnings</div>
            {readinessWarnings.map((code) => (
              <div key={`warning-${code}`} className="border rounded p-2">
                <div>{formatBlockerCode(code)}</div>
                <div className="small text-white-50">{code}</div>
              </div>
            ))}
          </div>
        ) : null}
        {readiness.viewerSupport?.checkedUrl ? (
          <>
            <div className="small text-white-50">Viewer check target</div>
            <div className="small mb-3 text-break">{readiness.viewerSupport.checkedUrl}</div>
          </>
        ) : null}
        <div className="small text-white-50">Checked at</div>
        <div className="mb-2">{readiness.checkedAt ? new Date(readiness.checkedAt).toLocaleString() : "N/A"}</div>
        {readinessExplanation.nextTierHint ? (
          <>
            <div className="small text-white-50 mt-3">Next tier hint</div>
            <div>{readinessExplanation.nextTierHint}</div>
          </>
        ) : null}

        <div className="d-grid gap-2 mt-3">
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
            onClick={onValidate}
            disabled={operationalActionsDisabled || runningArtworkValidation}
          >
            {runningArtworkValidation ? "Validating..." : "Validate Public Links"}
          </Button>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
            onClick={onRecompute}
            disabled={operationalActionsDisabled || runningArtworkRecompute}
          >
            {runningArtworkRecompute ? "Recomputing..." : "Recompute Readiness"}
          </Button>
        </div>

        {dirtyActionMessage || disabledForScope ? (
          <div className="small text-white-50 mt-3">
            {dirtyActionMessage || "QA actions are disabled outside the supported 3D publishing scope."}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );

  const stateTransitionsSection = (
    <div className="border-top pt-3 mt-3">
      <div className="fw-semibold mb-3">State Transitions</div>
      <div className="d-grid gap-2">
        <Button
          variant="light"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
          disabled={operationalActionsDisabled || !canMarkReadyNow || runningArtworkTransition}
          onClick={() => setPendingTransition("ready")}
        >
          Mark Ready
        </Button>
        <Button
          variant="light"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
          disabled={operationalActionsDisabled || !canReturnToDraft(state) || runningArtworkTransition}
          onClick={() => setPendingTransition("draft")}
        >
          Return to Draft
        </Button>
        <Button
          variant="light"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
          disabled={operationalActionsDisabled || !canPublishNow || runningArtworkTransition}
          onClick={() => setPendingTransition("published")}
        >
          Publish
        </Button>
        <Button
          variant="light"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.cardDangerAction}`}
          disabled={operationalActionsDisabled || !canWithdraw(state) || runningArtworkTransition}
          onClick={() => setPendingTransition("withdrawn")}
        >
          Withdraw
        </Button>
        <div className="small text-white-50 mt-2">
          {dirtyActionMessage
            ? dirtyActionMessage
            : disabledForScope
              ? "Transitions are disabled outside the supported 3D publishing scope."
              : state === "draft"
                ? (!readiness.targetMet
                    ? "Mark Ready is disabled until the current publication target is met."
                    : !publicationAuthorityKnown
                      ? "Set publication authority before marking the record ready."
                      : "Record can be marked ready.")
                : state === "ready"
                  ? (!readiness.targetMet
                      ? "Publish is disabled until the current publication target is met."
                      : !publicationAuthorityKnown
                        ? "Set publication authority before publishing the record."
                        : "Record can be published.")
                  : state === "published"
                    ? "Published records can be withdrawn."
                    : !readiness.targetMet
                      ? "Return to Draft is available now. Mark Ready and Publish require the publication target to be met."
                      : !publicationAuthorityKnown
                        ? "Return to Draft is available now. Mark Ready and Publish require publication authority."
                        : "Withdrawn records can be moved back to draft, ready, or published once the checks pass."}
        </div>
      </div>
    </div>
  );

  const opsRail = (
    <div className="d-flex flex-column gap-4">
      {readinessCard}

      <Card bg="dark" text="light">
        <Card.Header>Current Publication Blockers</Card.Header>
        <Card.Body>
          {displayedBaselineBlockers.length === 0 && currentTargetBlockers.length === 0 ? (
            <div className="text-white-50">Current target met. No baseline or target-level blockers are active.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {displayedBaselineBlockers.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  <div className="small text-uppercase text-white-50">Baseline blockers</div>
                  {displayedBaselineBlockers.map((code) => (
                    <div key={`baseline-${code}`} className="border rounded p-2">
                      <div>{formatBlockerCode(code)}</div>
                      <div className="small text-white-50">{code}</div>
                    </div>
                  ))}
                </div>
              ) : null}
              {currentTargetBlockers.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  <div className="small text-uppercase text-white-50">Target blockers</div>
                  {currentTargetBlockers.map((code) => (
                    <div key={`current-${code}`} className="border rounded p-2">
                      <div>{formatBlockerCode(code)}</div>
                      <div className="small text-white-50">{code}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card bg="dark" text="light">
        <Card.Header>Higher-Tier Gaps</Card.Header>
        <Card.Body>
          {higherTierGaps.length === 0 ? (
            <div className="text-white-50">No additional higher-tier gaps are currently flagged.</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {higherTierGaps.map((code) => (
                <div key={`higher-${code}`} className="border rounded p-2">
                  <div>{formatBlockerCode(code)}</div>
                  <div className="small text-white-50">{code}</div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card bg="dark" text="light">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span>Media Validation</span>
            {artworkValidation ? (
              <Badge bg={artworkValidation.passed ? "success" : "danger"}>
                {artworkValidation.passed ? "Pass" : "Issues"}
              </Badge>
            ) : null}
          </div>
          {artworkValidation?.issues?.length ? (
            <Button
              variant="light"
              size="sm"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
              onClick={() => setShowMediaValidationDetails((current) => !current)}
            >
              {showMediaValidationDetails ? "Hide issues" : "Show issues"}
            </Button>
          ) : null}
        </Card.Header>
        <Card.Body>
          {!artworkValidation ? (
            <div className="text-white-50">Run validation to check landing pages, oEmbed, preview media, and direct model delivery.</div>
          ) : (
            <>
              <div className="d-flex gap-2 flex-wrap mb-3">
                <Badge bg={artworkValidation.passed ? "success" : "danger"}>{artworkValidation.passed ? "Ready" : "Needs attention"}</Badge>
                <Badge bg="secondary">{artworkValidation.errorCount} errors</Badge>
                <Badge bg="secondary">{artworkValidation.warningCount} warnings</Badge>
              </div>
              <div className="small text-white-50">
                Checked at {new Date(artworkValidation.checkedAt).toLocaleString()}
              </div>
              {artworkValidation.issues.length === 0 ? (
                <div className="text-white-50 mt-2">No validation issues were found.</div>
              ) : null}
              <Collapse in={showMediaValidationDetails}>
                <div className="d-flex flex-column gap-2 mt-3">
                  {artworkValidation.issues.map((issue) => (
                    <div key={`${issue.code}-${issue.url || issue.finalUrl || "issue"}`} className="border rounded p-2">
                      <div className="d-flex justify-content-between gap-2">
                        <div>{formatValidationIssueCode(issue.code)}</div>
                        <Badge bg={issue.level === "error" ? "danger" : "warning"}>{issue.level}</Badge>
                      </div>
                      <div className="small text-white-50">{issue.message}</div>
                      {issue.url ? <div className="small text-break mt-1">{issue.url}</div> : null}
                      {shouldShowValidationIssueContentType(issue) ? (
                        <div className="small text-white-50">content-type: {issue.contentType}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </Collapse>
            </>
          )}
          {stateTransitionsSection}
        </Card.Body>
      </Card>

      <Card bg="dark" text="light">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Recent Publication History</span>
          {artworkDetail.recentTransitions.length > 0 ? (
            <Button
              variant="light"
              size="sm"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
              onClick={() => setShowRecentHistory((current) => !current)}
            >
              {showRecentHistory ? "Hide" : "Show"}
            </Button>
          ) : null}
        </Card.Header>
        <Card.Body>
          {artworkDetail.recentTransitions.length === 0 ? (
            <div className="text-white-50">No publication transitions have been recorded for this record yet.</div>
          ) : (
            <>
              <div className="text-white-50 small">Latest transition: {artworkDetail.recentTransitions[0].fromState} -&gt; {artworkDetail.recentTransitions[0].toState}</div>
              {showRecentHistory ? (
                <div className="d-flex flex-column gap-2 mt-3">
                  {artworkDetail.recentTransitions.map((transition) => (
                    <div key={`${transition.timestamp || "unknown"}-${transition.toState}`} className="border rounded p-2">
                      <div className="d-flex justify-content-between gap-2">
                        <div>{transition.fromState} -&gt; {transition.toState}</div>
                        <Badge bg="secondary">{transition.profileId || "default"}</Badge>
                      </div>
                      <div className="small text-white-50">
                        {transition.timestamp ? new Date(transition.timestamp).toLocaleString() : "Unknown time"}
                      </div>
                      {transition.reason ? <div className="small text-white-50">Reason: {transition.reason}</div> : null}
                      {transition.actorEmail || transition.actorUserId ? (
                        <div className="small text-white-50">
                          Actor: {transition.actorEmail || transition.actorUserId}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      {debugSection}
    </div>
  );

  const segments = [
    {
      id: "core-metadata" as ArtworkWorkspaceSegmentId,
      label: "Core Metadata",
      subtitle: "Author the record-level metadata that exports directly on the ProvidedCHO.",
      content: coreMetadataContent,
    },
    {
      id: "context" as ArtworkWorkspaceSegmentId,
      label: "Context",
      subtitle: "Manage authority-linked contextual entities and import suggestions.",
      content: contextEditorCard,
    },
    {
      id: "delivery" as ArtworkWorkspaceSegmentId,
      label: "Delivery",
      subtitle: "Review preview media, public delivery, rights, and higher-tier WebResource metadata.",
      content: deliveryContent,
    },
    {
      id: "process" as ArtworkWorkspaceSegmentId,
      label: "Process",
      subtitle: "Capture paradata and creation-process notes for the 3D workflow.",
      content: processContent,
    },
  ];

  return (
    <>
      {dataRouterContext ? <EdmUnsavedChangesPrompt when={isDirty} message={UNSAVED_CHANGES_MESSAGE} /> : null}
      {embedded ? (
        <div className="d-flex flex-column gap-3 text-white">
          {pageAlerts ? <div className="d-flex flex-column gap-3">{pageAlerts}</div> : null}
          <div className={`${workspaceTheme.topTabs} nav nav-tabs`} role="tablist" aria-label="Publishing sections">
            {segments.map((segment) => {
              const isActive = segment.id === activeSegment;
              return (
                <button
                  key={segment.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`nav-link ${isActive ? "active" : ""}`}
                  onClick={() => setActiveSegment(segment.id as ArtworkWorkspaceSegmentId)}
                >
                  {segment.label}
                </button>
              );
            })}
          </div>
          {segments.find((segment) => segment.id === activeSegment)?.subtitle ? (
            <div className={workspaceTheme.secondaryText}>
              {segments.find((segment) => segment.id === activeSegment)?.subtitle}
            </div>
          ) : null}
          <div className={workspaceTheme.summaryGrid}>
            <div className={workspaceTheme.summaryColumn}>
              <div className={workspaceTheme.detailSection}>
                {segments.find((segment) => segment.id === activeSegment)?.content}
              </div>
            </div>
            <div className={workspaceTheme.summaryColumn}>
              {opsRail}
            </div>
          </div>
        </div>
      ) : (
        <EdmWorkspaceShell
          header={pageHeader}
          alerts={pageAlerts}
          overview={overviewCard}
          segments={segments}
          activeSegment={activeSegment}
          onSegmentChange={(segmentId) => setActiveSegment(segmentId as ArtworkWorkspaceSegmentId)}
          rail={opsRail}
        />
      )}

      {isDirty ? (
        <div
          className="d-flex align-items-center justify-content-between gap-3 flex-wrap px-3 py-3 mt-3 rounded border"
          style={{
            background: "rgba(17, 24, 39, 0.96)",
            borderColor: "rgba(250, 204, 21, 0.32)",
            position: "sticky",
            bottom: 16,
            zIndex: 20,
          }}
        >
          <div className="small" style={{ color: artworkSaveError ? "#fca5a5" : "#facc15", fontWeight: 600 }}>
            {artworkSaveError || "Unsaved changes are pending."}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="light"
              size="lg"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
              onClick={onSave}
              disabled={metadataReadOnly || savingArtworkProfile || defaultLanguageSaveBlocked}
            >
              {savingArtworkProfile ? "Saving Artwork Details..." : "Save Artwork Details"}
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        show={Boolean(pendingTransition)}
        onHide={() => setPendingTransition(null)}
        onConfirm={confirmTransition}
        title="Confirm state transition"
        message={(
          <div className="d-flex flex-column gap-2">
            <div>Move this 3D Europeana record from <strong>{state}</strong> to <strong>{pendingTransition || "the selected state"}</strong>?</div>
            <div className="small text-white-50">Effective profile: {artworkDetail.effectiveProfile?.profileId || "default"}</div>
            <div className="small text-white-50">Readiness target: {readiness.targetMet ? "met" : "not met"}</div>
            {!readiness.targetMet ? <div className="small text-white-50">Current blocker count: {blockingIssueCount}</div> : null}
          </div>
        )}
        confirmText={runningArtworkTransition ? "Working..." : "Confirm"}
        cancelText="Cancel"
      />
    </>
  );
};
